import MeaPage from "../../../components/MeaPage";
import { useRef, useState, useEffect } from "react";
import { useLoaderData, useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { Editor } from "primereact/editor";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import campaignsAPI from "../../../services/campaigns";
import levelsAPI from "../../../services/levels";
import classesAPI from "../../../services/classes";
import { Dialog } from "primereact/dialog";
import { MultiSelect } from "primereact/multiselect";
import { ListBox } from "primereact/listbox";
import { Message } from "primereact/message";
import { Divider } from "primereact/divider";

const RegistrationNewCampaignPage = () => {
  const { registration } = useLoaderData();

  const [emailMessage, setEmailMessage] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [recipientsPreRegistered, setRecipientsPreRegistered] = useState(false);
  const [recipientsConfirmed, setRecipientsConfirmed] = useState(false);
  const [recipientsFilterConfirmedNotPaid, setRecipientsFilterConfirmedNotPaid] = useState(false);
  const [recipientsUnregistered, setRecipientsUnregistered] = useState(false);
  const [recipientsWaitingList, setRecipientsWaitingList] = useState(false);
  const [recipientsNewFiles, setRecipientsNewFiles] = useState(false);
  const [recipientsTeachers, setRecipientsTeachers] = useState(false);
  const [recipientsFilterTeachersOnlyThisTraining, setRecipientsFilterTeachersOnlyThisTraining] = useState(false);
  const [recipientsSecretaries, setRecipientsSecretaries] = useState(false);
  const [recipientsAdministrators, setRecipientsAdministrators] = useState(false);
  const [sendAllFamilies, setSendAllFamilies] = useState(true);
  const [selectedLevelIds, setSelectedLevelIds] = useState([]);
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  const [includeRegistrationLink, setIncludeRegistrationLink] = useState(true);
  const [dryRun, setDryRun] = useState(false);

  const navigate = useNavigate();

  const params = useParams();
  const schoolId = params.schoolId;
  const registrationId = params.registrationId;
  const accessToken = "";

  const [loading, setLoading] = useState(false);
  const toast = useRef();

  const [campaignDestinations, setCampaignDestinations] = useState([]);
  const [campaignDestinationsLoading, setCampaignDestinationsLoading] = useState(false);
  const [campaignDestinationDialogVisible, setCampaignDestinationDialogVisible] = useState(false);

  const [destinationFilter, setDestinationFilter] = useState("");

  const levelsQuery = levelsAPI.useGetList(schoolId, registrationId, accessToken, 1, 1000);
  const classesQuery = classesAPI.useGetList(schoolId, registrationId, accessToken, 1, 1000, {});
  const availableLevels = levelsQuery?.data?.data || [];
  const availableClasses = (classesQuery?.data?.data || []).filter((classItem) => {
    if (selectedLevelIds.length === 0) {
      return true;
    }
    return selectedLevelIds.includes(classItem.level_id);
  });

  useEffect(() => {
    if (selectedLevelIds.length === 0) {
      return;
    }
    setSelectedClassIds((previous) =>
      previous.filter((classId) =>
        availableClasses.some((availableClass) => availableClass.id === classId)
      )
    );
  }, [selectedLevelIds, availableClasses]);

  const recipientsLists = [
    recipientsPreRegistered ? "PREREGISTRED" : null,
    recipientsConfirmed ? "ENROLLED" : null,
    recipientsUnregistered ? "UNREGISTRED" : null,
    recipientsWaitingList ? "WAITING_LIST" : null,
    recipientsNewFiles ? "NEW" : null,
    recipientsTeachers ? "TEACHERS" : null,
    recipientsSecretaries ? "SECRETARIES" : null,
    recipientsAdministrators ? "ADMINISTRATORS" : null,
  ].filter(Boolean);

  const recipientsFilters = [
    recipientsFilterConfirmedNotPaid ? "FILTER_ENROLLED_NOT_PAID" : null,
    (recipientsTeachers && recipientsFilterTeachersOnlyThisTraining) ? "FILTER_TEACHERS_ONLY_THIS_TRAINING" : null,
  ].filter(Boolean);

  const hasStudentOrParentRecipients =
    recipientsPreRegistered ||
    recipientsConfirmed ||
    recipientsUnregistered ||
    recipientsWaitingList ||
    recipientsNewFiles;

  useEffect(() => {
    if (!hasStudentOrParentRecipients) {
      setIncludeRegistrationLink(false);
    } else {
      setIncludeRegistrationLink(true);
    }
  }, [hasStudentOrParentRecipients]);

  const selectedRecipientsCount = recipientsLists.length;
  const hasSubject = emailSubject.trim().length > 0;
  const hasMessage = (emailMessage || "").replace(/<[^>]*>/g, "").trim().length > 0;
  const hasConfirmedFamilyScopeSelection =
    !recipientsConfirmed ||
    sendAllFamilies ||
    selectedLevelIds.length > 0 ||
    selectedClassIds.length > 0;
  const canSubmit = hasSubject && hasMessage && selectedRecipientsCount > 0 && hasConfirmedFamilyScopeSelection;

  const setDefaultRecipients = () => {
    setRecipientsPreRegistered(false);
    setRecipientsConfirmed(true);
    setRecipientsFilterConfirmedNotPaid(false);
    setRecipientsUnregistered(false);
    setRecipientsWaitingList(false);
    setRecipientsNewFiles(false);
    setRecipientsTeachers(false);
    setRecipientsFilterTeachersOnlyThisTraining(false);
    setRecipientsSecretaries(false);
    setRecipientsAdministrators(false);
    setSendAllFamilies(true);
    setSelectedLevelIds([]);
    setSelectedClassIds([]);
  };

  const selectAllRecipients = () => {
    setRecipientsPreRegistered(true);
    setRecipientsConfirmed(true);
    setRecipientsFilterConfirmedNotPaid(false);
    setRecipientsUnregistered(true);
    setRecipientsWaitingList(true);
    setRecipientsNewFiles(true);
    setRecipientsTeachers(true);
    setRecipientsFilterTeachersOnlyThisTraining(false);
    setRecipientsSecretaries(true);
    setRecipientsAdministrators(true);
  };

  const handleGetCampaignDestinations = () => {
    setCampaignDestinationsLoading(true);

    campaignsAPI
      .GetCampaignDestinations(schoolId, registrationId, accessToken, {
        recipients_lists: recipientsLists.join(","),
        recipients_filters: recipientsFilters.join(","),
        send_all_families: sendAllFamilies,
        level_ids: sendAllFamilies ? [] : selectedLevelIds,
        class_ids: sendAllFamilies ? [] : selectedClassIds,
      })
      .then((data) => {
        setCampaignDestinations(data?.data || []);
        setCampaignDestinationDialogVisible(true);
      })
      .catch(() => {
        toast.current.show({
          severity: "error",
          summary: "Erreur",
          detail: "Impossible de récupérer les destinataires.",
        });
      })
      .finally(() => {
        setCampaignDestinationsLoading(false);
      });
  };

  const handleSave = () => {
    setLoading(true);

    campaignsAPI
      .CreateOne(schoolId, registrationId, accessToken, {
        subject: emailSubject,
        body: emailMessage,
        recipients_lists: recipientsLists.join(","),
        recipients_filters: recipientsFilters.join(","),
        send_all_families: sendAllFamilies,
        level_ids: sendAllFamilies ? [] : selectedLevelIds,
        class_ids: sendAllFamilies ? [] : selectedClassIds,
        include_personal_link: includeRegistrationLink,
        dry_run: dryRun,
      })
      .then((data) => {
        toast.current.show({
          severity: "success",
          summary: "Succes",
          detail: "La campagne a ete creee avec succes.",
        });
        const campaignId = data?.data?.id;
        navigate("/school/" + schoolId + "/admin/registrations/" + registrationId + "/campaigns/" + campaignId);
      })
      .catch(() => {
        toast.current.show({
          severity: "error",
          summary: "Erreur",
          detail: "Impossible de creer la campagne.",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleCancel = () => {
    navigate("/school/" + schoolId + "/admin/registrations/" + registrationId + "/campaigns");
    setEmailSubject("");
    setEmailMessage("");
    setDefaultRecipients();
    setSendAllFamilies(true);
    setSelectedLevelIds([]);
    setSelectedClassIds([]);
    setIncludeRegistrationLink(true);
  };

  const filteredDestinations = campaignDestinations.filter((destination) =>
    destination.toLowerCase().includes(destinationFilter.trim().toLowerCase())
  );

  return (
    <>
      <MeaPage title="Nouvelle campagne e-mail" showBackButton={false}>
        <Toast ref={toast} position="bottom-center" />
        <ConfirmDialog />

        <div className="grid">
          <div className="col-12 lg:col-8">
            <div className="card surface-section p-4 h-full">
              <div className="field mb-4">
                <label htmlFor="emailSubject" className="block mb-2 font-medium">
                  Objet
                </label>
                <InputText
                  id="emailSubject"
                  value={emailSubject}
                  onChange={(e) => {
                    setEmailSubject(e.target.value);
                  }}
                  className="w-full"
                  placeholder="Exemple: Informations importantes sur l'inscription"
                />
              </div>

              <div className="field mb-3">
                <div className="mb-2">
                  <label htmlFor="mailMessage" className="font-medium">
                    Message
                  </label>
                </div>

                <Editor
                  id="mailMessage"
                  value={emailMessage}
                  onTextChange={(e) => {
                    setEmailMessage(e.htmlValue);
                  }}
                  style={{ height: "320px" }}
                />
              </div>
            </div>
          </div>

          <div className="col-12 lg:col-4">
            <div className="card surface-section p-4 mb-3">
              <div className="flex align-items-center justify-content-between mb-3">
                <h4 className="m-0 text-base">Destinataires</h4>
                <span className="text-color-secondary text-sm">{selectedRecipientsCount} selection(s)</span>
              </div>

              <div className="flex gap-2 mb-3">
                <Button
                  type="button"
                  size="small"
                  text
                  label="Tout selectionner"
                  onClick={selectAllRecipients}
                />
                <Button
                  type="button"
                  size="small"
                  text
                  label="Par defaut"
                  onClick={setDefaultRecipients}
                />
              </div>

              <div className="flex flex-column gap-2">
                <div className="field-checkbox m-0">
                  <Checkbox
                    inputId="preRegistered"
                    checked={recipientsPreRegistered}
                    onChange={(e) => {
                      setRecipientsPreRegistered(e.target.checked);
                    }}
                  />
                  <label htmlFor="preRegistered" className="ml-2">
                    Préinscrits
                  </label>
                </div>

                <div className="field-checkbox m-0">
                  <Checkbox
                    inputId="confirmed"
                    checked={recipientsConfirmed}
                    onChange={(e) => {
                      setRecipientsConfirmed(e.target.checked);
                      if (!e.target.checked) {
                        setRecipientsFilterConfirmedNotPaid(false);
                      }
                    }}
                  />
                  <label htmlFor="confirmed" className="ml-2">
                    Confirmés (inscripts)
                  </label>
                </div>

                {recipientsConfirmed && (
                  <>
                    <div className="field-checkbox m-0 ml-4">
                      <Checkbox
                        inputId="ENROLLED_not_paid"
                        checked={recipientsFilterConfirmedNotPaid}
                        onChange={(e) => {
                          setRecipientsFilterConfirmedNotPaid(e.target.checked);
                        }}
                      />
                      <label htmlFor="ENROLLED_not_paid" className="ml-2 text-sm">
                        Uniquement avec contrat impayé
                      </label>
                    </div>

                    <div className="field-checkbox m-0 ml-4 mt-2">
                      <Checkbox
                        inputId="send_all_families"
                        checked={sendAllFamilies}
                        onChange={(e) => {
                          setSendAllFamilies(e.target.checked);
                          if (e.target.checked) {
                            setSelectedLevelIds([]);
                            setSelectedClassIds([]);
                          }
                        }}
                      />
                      <label htmlFor="send_all_families" className="ml-2 text-sm">
                        Envoyer à toutes les classes de tous les niveaux
                      </label>
                    </div>

                    {!sendAllFamilies && (
                      <div className="ml-4 mt-2 flex flex-column gap-2">
                        <div>
                          <label className="block text-xs font-medium mb-1">Niveaux</label>
                          <MultiSelect
                            value={selectedLevelIds}
                            options={(availableLevels || []).map((level) => ({
                              label: level.name,
                              value: level.id,
                            }))}
                            optionLabel="label"
                            optionValue="value"
                            onChange={(e) => setSelectedLevelIds(e.value || [])}
                            className="w-full"
                            display="chip"
                            maxSelectedLabels={3}
                            selectedItemsLabel="{0} niveaux sélectionnés"
                            placeholder="Choisir un ou plusieurs niveaux"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium mb-1">Classes (ignore les niveaux si au moins une classe sélectionnée)</label>
                          <MultiSelect
                            value={selectedClassIds}
                            options={(availableClasses || []).map((classItem) => ({
                              label: classItem.name,
                              value: classItem.id,
                            }))}
                            optionLabel="label"
                            optionValue="value"
                            onChange={(e) => setSelectedClassIds(e.value || [])}
                            className="w-full"
                            display="chip"
                            maxSelectedLabels={3}
                            selectedItemsLabel="{0} classes sélectionnées"
                            placeholder="Choisir une ou plusieurs classes"
                            disabled={availableClasses.length === 0}
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}

                <div className="field-checkbox m-0">
                  <Checkbox
                    inputId="unregistered"
                    checked={recipientsUnregistered}
                    onChange={(e) => {
                      setRecipientsUnregistered(e.target.checked);
                    }}
                  />
                  <label htmlFor="unregistered" className="ml-2">
                    Désinscrits
                  </label>
                </div>

                <div className="field-checkbox m-0">
                  <Checkbox
                    inputId="waitingList"
                    checked={recipientsWaitingList}
                    onChange={(e) => {
                      setRecipientsWaitingList(e.target.checked);
                    }}
                  />
                  <label htmlFor="waitingList" className="ml-2">
                    Liste d'attente
                  </label>
                </div>

                <div className="field-checkbox m-0">
                  <Checkbox
                    inputId="newFiles"
                    checked={recipientsNewFiles}
                    onChange={(e) => {
                      setRecipientsNewFiles(e.target.checked);
                    }}
                  />
                  <label htmlFor="newFiles" className="ml-2">
                    Nouvelles candidatures
                  </label>
                </div>

                <Divider className="my-2" />

                <div className="field-checkbox m-0">
                  <Checkbox
                    inputId="teachers"
                    checked={recipientsTeachers}
                    onChange={(e) => {
                      setRecipientsTeachers(e.target.checked);
                      if (!e.target.checked) {
                        setRecipientsFilterTeachersOnlyThisTraining(false);
                      }
                    }}
                  />
                  <label htmlFor="teachers" className="ml-2">
                    Enseignants
                  </label>
                </div>

                {recipientsTeachers && (
                  <div className="field-checkbox m-0 ml-4">
                    <Checkbox
                      inputId="teachers_only_this_training"
                      checked={recipientsFilterTeachersOnlyThisTraining}
                      onChange={(e) => {
                        setRecipientsFilterTeachersOnlyThisTraining(e.target.checked);
                      }}
                    />
                    <label htmlFor="teachers_only_this_training" className="ml-2 text-sm">
                      Uniquement les enseignants de cette formation
                    </label>
                  </div>
                )}

                <div className="field-checkbox m-0">
                  <Checkbox
                    inputId="secretaries"
                    checked={recipientsSecretaries}
                    onChange={(e) => {
                      setRecipientsSecretaries(e.target.checked);
                    }}
                  />
                  <label htmlFor="secretaries" className="ml-2">
                    Secrétaires
                  </label>
                </div>

                <div className="field-checkbox m-0">
                  <Checkbox
                    inputId="administrators"
                    checked={recipientsAdministrators}
                    onChange={(e) => {
                      setRecipientsAdministrators(e.target.checked);
                    }}
                  />
                  <label htmlFor="administrators" className="ml-2">
                    Équipe administrative
                  </label>
                </div>
              </div>

              {selectedRecipientsCount === 0 && (
                <Message
                  className="mt-3"
                  severity="warn"
                  text="Selectionnez au moins un groupe de destinataires."
                />
              )}

              {!hasConfirmedFamilyScopeSelection && recipientsConfirmed && (
                <Message
                  className="mt-3"
                  severity="warn"
                  text="Selectionnez au moins un niveau ou une classe pour les familles confirmées."
                />
              )}

            </div>

            <div className="card surface-section p-4">
              <h4 className="m-0 mb-3 text-base">Options d'envoi</h4>

              <div className="field-checkbox m-0 mb-3">
                <Checkbox
                  inputId="includeRegistrationLink"
                  checked={includeRegistrationLink}
                  onChange={(e) => {
                    setIncludeRegistrationLink(e.target.checked);
                  }}
                  disabled={!hasStudentOrParentRecipients}
                />
                <label htmlFor="includeRegistrationLink" className={`ml-2 ${!hasStudentOrParentRecipients ? "text-color-secondary" : ""}`}>
                  Inclure le lien personnel vers l'espace famille
                </label>
              </div>

              <div className="field-checkbox m-0">
                <Checkbox
                  inputId="dryRun"
                  checked={dryRun}
                  onChange={(e) => {
                    setDryRun(e.target.checked);
                  }}
                />
                <label htmlFor="dryRun" className="ml-2">
                  Mode test
                </label>
              </div>

              {dryRun && (
                <Message
                  className="mt-3"
                  severity="info"
                  text="Simulation active: aucun e-mail reel ne sera envoye."
                />
              )}
            </div>
          </div>

          <div className="col-12">
            <div className="card surface-section p-3 flex flex-wrap gap-2 justify-content-end">
              <Button
                outlined
                icon="pi pi-users"
                label="Apercu des e-mails"
                onClick={handleGetCampaignDestinations}
                loading={campaignDestinationsLoading}
                disabled={selectedRecipientsCount === 0 || (!hasConfirmedFamilyScopeSelection && recipientsConfirmed)}
              />
              <Button
                outlined
                label="Annuler"
                onClick={handleCancel}
                disabled={loading}
              />
              <Button
                label="Creer la campagne"
                onClick={handleSave}
                loading={loading}
                disabled={!canSubmit}
                icon="pi pi-send"
              />
            </div>
          </div>
        </div>

        <Dialog
          modal
          header="Liste des destinataires"
          visible={campaignDestinationDialogVisible}
          style={{ width: "min(860px, 95vw)" }}
          onHide={() => {
            setCampaignDestinationDialogVisible(false);
            setDestinationFilter("");
          }}
        >
          <div className="field mb-3">
            <InputText
              id="destinationFilter"
              value={destinationFilter}
              onChange={(e) => setDestinationFilter(e.target.value)}
              placeholder="Rechercher un e-mail"
              className="w-full"
            />
          </div>

          <Message
            className="mb-3"
            severity="info"
            text={filteredDestinations.length + " destinataire(s) affiche(s)"}
          />

          <ListBox
            options={filteredDestinations.map((destination) => ({
              label: destination,
              value: destination,
            }))}
            optionLabel="label"
            optionValue="value"
            listStyle={{ maxHeight: "320px" }}
            className="w-full"
          />
        </Dialog>
      </MeaPage>
    </>
  );
};

export default RegistrationNewCampaignPage;
